// Notes: Markdown kept in the browser, per folder. Saved to IndexedDB shortly after each change.
import { db, type NoteRecord } from '../lib/db';
import { useWorkspace } from './workspace';

const saveTimers = new Map<string, number>();

function put(note: NoteRecord): void {
  useWorkspace.setState((s) => ({ notes: { ...s.notes, [note.id]: note } }));
}

function freeTitle(wanted: string): string {
  const taken = new Set(Object.values(useWorkspace.getState().notes).map((n) => n.title.toLowerCase()));
  let title = wanted;
  for (let i = 2; taken.has(title.toLowerCase()); i++) title = `${wanted} ${i}`;
  return title;
}

export async function createNote(title = 'Untitled', body = ''): Promise<NoteRecord | null> {
  const source = useWorkspace.getState().source;
  if (!source) return null;
  const now = Date.now();
  const note: NoteRecord = { id: crypto.randomUUID(), sourceId: source.id, title: freeTitle(title), body, created: now, updated: now };
  put(note);
  await db.notes.put(note);
  return note;
}

/** A note laid out for deciding on a design for one use case. */
export function useCaseTemplate(title: string): string {
  return [
    `Use case: **${title}**`,
    '',
    '## Goal',
    'What should the model make possible? Who needs it, and how often?',
    '',
    '## Reads and writes',
    '- Reads: which entities are loaded together, filtered or sorted by what?',
    '- Writes: what changes together in one transaction? How many rows?',
    '',
    '## Constraints',
    '- Consistency, history, size, privacy, existing data to migrate.',
    '',
    '## Candidates',
    '- Link each sketch you consider, like `[[sketch:Its title]]`, with one sentence on the idea.',
    '',
    '## Decision',
    'Which candidate, and why. #decision',
    '',
  ].join('\n');
}

export function updateNote(id: string, patch: Partial<Pick<NoteRecord, 'title' | 'body'>>): void {
  const note = useWorkspace.getState().notes[id];
  if (!note || note.origin) return;
  const next = { ...note, ...patch, updated: Date.now() };
  put(next);
  window.clearTimeout(saveTimers.get(id));
  saveTimers.set(
    id,
    window.setTimeout(() => {
      saveTimers.delete(id);
      void db.notes.put(useWorkspace.getState().notes[id] ?? next);
    }, 300),
  );
}

export async function flushNotes(): Promise<void> {
  for (const id of [...saveTimers.keys()]) {
    window.clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
    const note = useWorkspace.getState().notes[id];
    if (note) await db.notes.put(note);
  }
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', () => void flushNotes());

/** An editable copy of a note (for notes that are files in the folder). */
export async function duplicateNote(id: string): Promise<NoteRecord | null> {
  const note = useWorkspace.getState().notes[id];
  return note ? createNote(note.title, note.body) : null;
}

export async function deleteNote(id: string): Promise<void> {
  window.clearTimeout(saveTimers.get(id));
  saveTimers.delete(id);
  useWorkspace.setState((s) => {
    const notes = { ...s.notes };
    delete notes[id];
    return { notes, activeNote: s.activeNote === id ? null : s.activeNote };
  });
  await db.notes.delete(id);
}

export function setActiveNote(id: string | null): void {
  useWorkspace.setState({ activeNote: id });
}
